import type { TimeEntry } from './domain.js';
import { businessDayOf } from './domain.js';

// ── Timeshift report computation (S3 — parent brief §5.3, payroll semantics) ─
//
//  * days worked  = days with ≥ 1 clock-in
//  * full days off = days in the (contract-clamped) range with no clock-in
//  * work hours   = sum of shift durations; an OPEN shift contributes 0 and is
//    flagged (« à corriger ») — the operator fixes it before the payroll run.
//
// A shift belongs to the BUSINESS day of its clock-in — Europe/Paris with the
// 04:00 cutoff (businessDayOf). Restaurant shifts cross midnight routinely:
// 18:00 → 03:00 is ONE working day of 9 h credited to Day 1, leaving Day 2 at
// 0 h (a rest day, unless a fresh shift starts that evening). Midnight is
// deliberately NOT the boundary; splitting there would double-count working
// days and corrupt « jours de repos ».
//
// Paris rather than the device's day matters because this runs in the browser
// (bos@ Pointages, staff@ Mes pointages) — a non-Paris device used to bucket
// by its own calendar and, at a month boundary, drop the shift out of the
// payroll range entirely (fixed 2026-07-20).
// Pure computation — the staff app renders it and the print view is just CSS.

export interface TimesheetShift {
  entryId: number;
  clockIn: string;
  clockOut: string | null;
  /** Decimal hours, 2 places (the Connecteam presentation) — null when open. */
  hours: number | null;
}

export interface TimesheetDay {
  /** Europe/Paris day key 'YYYY-MM-DD'. */
  day: string;
  /** Shifts oldest first within the day. */
  shifts: TimesheetShift[];
  totalHours: number;
  hasOpenEntry: boolean;
}

export interface Timesheet {
  /** Effective range after contract clamping, inclusive. */
  fromDay: string;
  toDay: string;
  /** Worked days only, NEWEST first (the report body). */
  days: TimesheetDay[];
  totalHours: number;
  daysWorked: number;
  /** Full days off = calendar days in range − days worked. */
  daysOff: number;
  /** Open (uncorrected) entries in range — must be 0 before a payroll run. */
  openEntryCount: number;
}

export interface TimesheetRange {
  fromDay: string;
  toDay: string;
}

export interface ContractWindow {
  startDate?: string | null;
  endDate?: string | null;
}

/** Inclusive calendar-day count between two 'YYYY-MM-DD' keys (noon dodges DST). */
function calendarDays(fromDay: string, toDay: string): number {
  const from = new Date(`${fromDay}T12:00:00`).getTime();
  const to = new Date(`${toDay}T12:00:00`).getTime();
  return Math.round((to - from) / 86_400_000) + 1;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function computeTimesheet(
  entries: TimeEntry[],
  range: TimesheetRange,
  contract: ContractWindow = {},
): Timesheet {
  // Clamp the requested range to the contract window when dates are known
  // (a CDD's off-days are only meaningful inside the contract).
  const fromDay =
    contract.startDate && contract.startDate > range.fromDay ? contract.startDate : range.fromDay;
  const toDay = contract.endDate && contract.endDate < range.toDay ? contract.endDate : range.toDay;

  if (fromDay > toDay) {
    return { fromDay, toDay, days: [], totalHours: 0, daysWorked: 0, daysOff: 0, openEntryCount: 0 };
  }

  const byDay = new Map<string, TimesheetShift[]>();
  let openEntryCount = 0;

  for (const entry of entries) {
    const day = businessDayOf(new Date(entry.clockIn));
    if (day < fromDay || day > toDay) continue;
    const hours =
      entry.clockOut === null
        ? null
        : round2((Date.parse(entry.clockOut) - Date.parse(entry.clockIn)) / 3_600_000);
    if (hours === null) openEntryCount++;
    const shifts = byDay.get(day) ?? [];
    shifts.push({ entryId: entry.id, clockIn: entry.clockIn, clockOut: entry.clockOut, hours });
    byDay.set(day, shifts);
  }

  const days: TimesheetDay[] = [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([day, shifts]) => {
      shifts.sort((a, b) => (a.clockIn < b.clockIn ? -1 : 1));
      return {
        day,
        shifts,
        totalHours: round2(shifts.reduce((sum, s) => sum + (s.hours ?? 0), 0)),
        hasOpenEntry: shifts.some((s) => s.hours === null),
      };
    });

  const daysWorked = days.length;
  return {
    fromDay,
    toDay,
    days,
    totalHours: round2(days.reduce((sum, d) => sum + d.totalHours, 0)),
    daysWorked,
    daysOff: calendarDays(fromDay, toDay) - daysWorked,
    openEntryCount,
  };
}

/** One line of the full-period report: a worked day, or a rest day. */
export interface TimesheetCalendarRow {
  day: string;
  /** null on a rest day — no clock-in at all (the « jours de repos » count). */
  worked: TimesheetDay | null;
}

/**
 * Every day of the (already contract-clamped) period, newest first — worked
 * days interleaved with rest days.
 *
 * The report used to list only worked days, which made a month of payroll
 * impossible to scan for absences: a missing day and a day that simply had no
 * row looked identical. Rendering every day makes « jours de repos » visible
 * and auditable rather than just a headline number, and the two agree by
 * construction (rest rows here === Timesheet.daysOff).
 *
 * Ordering matches Timesheet.days (newest first) so the existing report reads
 * the same way.
 */
export function timesheetCalendar(ts: Timesheet): TimesheetCalendarRow[] {
  const worked = new Map(ts.days.map((d) => [d.day, d]));
  const rows: TimesheetCalendarRow[] = [];
  // Walk in UTC so the iteration is timezone-independent; the keys are already
  // business-day strings, this is pure calendar arithmetic over them.
  const cursor = new Date(`${ts.fromDay}T00:00:00Z`);
  const last = new Date(`${ts.toDay}T00:00:00Z`);
  while (cursor <= last) {
    const day = cursor.toISOString().slice(0, 10);
    rows.push({ day, worked: worked.get(day) ?? null });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  rows.reverse(); // newest first, matching Timesheet.days
  return rows;
}

/**
 * Per (day, employee) worked hours — the Présence matrix's bos@ hours overlay
 * (2026-07-19). Same convention as computeTimesheet (shift belongs to the
 * BUSINESS day of its clock-in — Paris, 04:00 cutoff; an open shift
 * contributes 0, flagged elsewhere) but bucketed by EMPLOYEE too, across
 * potentially many people at once. The two share one day convention, so an
 * 18:00→03:00 night shows its full 9 h on Day 1 in the Présence matrix and
 * the payroll report alike.
 *
 * Unlike computeTimesheet this does NOT range-filter — it buckets everything
 * handed to it, so callers may over-fetch safely (they do, to cover the
 * cutoff overhang) and simply not read the out-of-range keys.
 */
export function computeDayWorkedHours(
  entries: Pick<TimeEntry, 'employeeId' | 'clockIn' | 'clockOut'>[],
): Map<string, Map<string, number>> {
  const byDay = new Map<string, Map<string, number>>();
  for (const entry of entries) {
    const day = businessDayOf(new Date(entry.clockIn));
    const hours =
      entry.clockOut === null ? 0 : round2((Date.parse(entry.clockOut) - Date.parse(entry.clockIn)) / 3_600_000);
    const byEmployee = byDay.get(day) ?? new Map<string, number>();
    byEmployee.set(entry.employeeId, round2((byEmployee.get(entry.employeeId) ?? 0) + hours));
    byDay.set(day, byEmployee);
  }
  return byDay;
}
